import { IntegrationKind } from "@redotech/redo-integrations-core";
import { useEffect, useState } from "react";
import { FrameworkIntegrationApi } from "./checkout-widgets-builder-context";

export type FrameworkIntegrationStatus =
  | { state: "loading" }
  | { state: "connected" }
  | { state: "disconnected" };

/**
 * Polls an integration's connection status via {@link FrameworkIntegrationApi}.
 * Generic across any `redo/integration` framework integration (Judge.me,
 * Yotpo, …); the per-provider connection hooks have been retired.
 */
export function useFrameworkIntegrationStatus(
  api: FrameworkIntegrationApi | undefined,
  kind: IntegrationKind,
): {
  status: FrameworkIntegrationStatus;
  setStatus: (status: FrameworkIntegrationStatus) => void;
  refresh: () => Promise<void>;
} {
  const [status, setStatus] = useState<FrameworkIntegrationStatus>({
    state: "loading",
  });

  useEffect(() => {
    if (!api) {
      setStatus({ state: "disconnected" });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const result = await api.getStatus(kind);
        if (cancelled) {
          return;
        }
        setStatus(
          result.connected ? { state: "connected" } : { state: "disconnected" },
        );
      } catch {
        if (!cancelled) {
          setStatus({ state: "disconnected" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, kind]);

  async function refresh() {
    if (!api) {
      return;
    }
    const result = await api.getStatus(kind);
    setStatus(
      result.connected ? { state: "connected" } : { state: "disconnected" },
    );
  }

  return { status, setStatus, refresh };
}
